import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import { FiEdit3, FiTrash2 } from "react-icons/fi";

const Wrap = styled.div`
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
  background: #fafbfc;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &.active {
    border-color: #1e3a5f;
  }

  .pad-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid #eef1f4;
    font-size: 0.8em;
    color: #64748b;

    .hint {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    button {
      display: flex;
      align-items: center;
      gap: 5px;
      background: transparent;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 4px 10px;
      font-size: 0.85em;
      color: #64748b;
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        border-color: #dc2626;
        color: #dc2626;
      }
    }
  }

  canvas {
    display: block;
    width: 100%;
    height: 180px;
    touch-action: none;
    cursor: crosshair;
  }
`;

const SignaturePad = forwardRef(function SignaturePad({ onChange }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
    canvasRef.current.parentElement.classList.add("active");
  }, []);

  const move = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);

    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    last.current = pos;
    if (!hasDrawn.current) {
      hasDrawn.current = true;
      onChange && onChange(true);
    }
  }, [onChange]);

  const end = useCallback(() => {
    drawing.current = false;
    canvasRef.current?.parentElement.classList.remove("active");
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    // Resolução interna fixa; o CSS controla o tamanho exibido
    canvas.width = 800;
    canvas.height = 240;

    canvas.addEventListener("pointerdown", start);
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerup", end);
    canvas.addEventListener("pointerleave", end);
    return () => {
      canvas.removeEventListener("pointerdown", start);
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerup", end);
      canvas.removeEventListener("pointerleave", end);
    };
  }, [start, move, end]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
    onChange && onChange(false);
  }, [onChange]);

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty() {
      return !hasDrawn.current;
    },
    toDataURL() {
      return hasDrawn.current ? canvasRef.current.toDataURL("image/png") : null;
    },
  }));

  return (
    <Wrap>
      <div className="pad-header">
        <span className="hint"><FiEdit3 /> Assine com o mouse ou o dedo</span>
        <button type="button" onClick={clear}>
          <FiTrash2 /> Limpar
        </button>
      </div>
      <canvas ref={canvasRef} />
    </Wrap>
  );
});

export default SignaturePad;
